import { BitmapLayer } from '@deck.gl/layers';
import { TileLayer, TileLayerProps } from './tileLayer';

type BoundingBox = {
    west: number;
    south: number;
    east: number;
    north: number;
};

export interface STACTileLayer extends TileLayer {
    itemId: string;
    parentTargetId: string;
    collectionId: string;
}

/**
 * Return a raster layer from Titiler
 */
export const getTitilerPgStacLayer = ({
    dataUrl,
    id,
    zoom,
    TileLayerProps,
    bounds,
    itemId,
    targetId,
    collectionId,
    visible = true,
    opacity
}: {
    dataUrl: string;
    id: string;
    zoom: number;
    TileLayerProps: Partial<TileLayerProps>;
    bounds: number[];
    itemId: string;
    targetId: string;
    collectionId: string;
    visible: boolean;
    opacity: number;
}) => {
    // Return layer
    const tileLayer = new TileLayer({
        id,
        data: dataUrl,
        minZoom: 0,
        // maxZoom is lowered during interaction to display lower-zoom tiles
        // and limit fetching while the user moves the map.
        maxZoom: zoom,
        tileSize: 512,
        extent: bounds,
        opacity,
        zoomOffset: 0,
        pickable: true,
        visible,
        // When zooming in and out, this option does not overlap
        // old tiles with new ones. The default strategy would overlay
        // old tiles with new tiles and since the colors are semi-transparent,
        // there was an artifact of darker tiles appearing.
        // https://deck.gl/docs/api-reference/geo-layers/tile-layer#refinementstrategy
        refinementStrategy: 'no-overlap',
        renderSubLayers: (props) => {
            // handles cases (such as 404 error for a tile) where there is no data
            if (!props.data || !props.tile) return [];

            // some tiles seem to come with {bottom, left, top, right} in
            // their props.tile.bbox instead of {west, south, east, north}
            // these cause rendering errors, and don't seem to be in tiles
            // we should be requesting anyway
            const { west, south, east, north } = props.tile.bbox as BoundingBox;

            // use map+reduce to check for undefined coordinates in the bounding box
            const anyBBoxCoordsUndefined = [west, south, east, north]
                .map((coord) => typeof coord === 'undefined')
                .reduce((acc, curr) => acc || curr, false);

            if (anyBBoxCoordsUndefined) return [];
            return [
                new BitmapLayer(props, {
                    data: undefined,
                    image: props.data,
                    bounds: [west, south, east, north],
                    pickable: true
                })
            ];
        },
        // A few optimizations to limit tile requests
        debounceTime: 175,
        maxRequests: 4,
        updateTriggers: {
            maxZoom: [zoom]
        },
        ...TileLayerProps
    }) as STACTileLayer;

    // set new tile layer's itemId to the STAC item's id
    // this assigns a new prop to the layer and
    // allows getting the STAC item's data on click
    tileLayer.itemId = itemId;
    tileLayer.parentTargetId = targetId;
    tileLayer.collectionId = collectionId;

    return tileLayer;
};
